import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/common/SEOHead";
import { SITE_URL } from "@/lib/constants/urls";
import { Button } from "@/components/ui/button";

interface Row {
  feature: string;
  paperverse: string;
  overleaf: string;
}

const rows: Row[] = [
  {
    feature: "AI co-author that drafts and revises sections",
    paperverse: "Yes. Ask for a section, a rewrite or a tighter abstract and it edits the manuscript directly.",
    overleaf: "No AI co-author in the core editor. You write the LaTeX yourself.",
  },
  {
    feature: "Literature search and citation insertion",
    paperverse: "Yes. Writer searches the literature, resolves real references, adds them to the bibliography and cites them in the text.",
    overleaf: "No built-in literature search. You add references manually or through an external reference manager.",
  },
  {
    feature: "LaTeX editor with live PDF preview",
    paperverse: "Yes, with automatic compiling after every accepted change.",
    overleaf: "Yes, this is Overleaf's core strength, with a mature editor and a large template library.",
  },
  {
    feature: "Compile error repair",
    paperverse: "Writer reads the compiler log and repairs broken LaTeX for you.",
    overleaf: "Compiler log is shown, and you fix the errors yourself.",
  },
  {
    feature: "AI peer review of the finished draft",
    paperverse: "Yes. Structured review with strengths, weaknesses and ratings across methodology, clarity, rigor and novelty.",
    overleaf: "Not part of the product.",
  },
  {
    feature: "Real-time multi-author co-editing",
    paperverse: "Not yet. Writer is built around one author working with an AI co-author.",
    overleaf: "Yes, simultaneous editing with co-authors is a core feature.",
  },
  {
    feature: "LaTeX source download",
    paperverse: "Yes on paid plans and for admins, as a zip with the .tex file and the bibliography.",
    overleaf: "Yes, projects can be downloaded as source.",
  },
  {
    feature: "Where your work is processed",
    paperverse: "Storage and AI processing stay inside the EU and EEA.",
    overleaf: "See Overleaf's own documentation for their hosting and data locations.",
  },
  {
    feature: "Free tier",
    paperverse: "Yes. 10 credits per month, with a small PaperVerse credit line on exported PDFs. Paid plans remove it.",
    overleaf: "Yes, a free plan is available alongside paid plans.",
  },
];

const faqs: { q: string; a: string }[] = [
  {
    q: "Can I bring my existing LaTeX from Overleaf?",
    a: "Yes. You can paste your LaTeX into the Writer editor and keep working on it there, and Writer will compile it and repair errors it finds along the way.",
  },
  {
    q: "Can I download the LaTeX source, or am I locked in?",
    a: "You can download the compiled PDF on every plan, and paid plans can download the full source as a zip with the .tex file and the bibliography, so your manuscript is always yours to take elsewhere.",
  },
  {
    q: "Does my draft stay private?",
    a: "Yes. A draft stays private until you decide to publish it on PaperVerse, it is not listed publicly, and it is not used to train AI models.",
  },
  {
    q: "Is it free to try?",
    a: "Yes. You can start writing without an account, and a free account includes 10 credits per month covering both writing and AI reviews.",
  },
  {
    q: "Are the citations real?",
    a: "Writer searches the literature and resolves each reference before it is added to the bibliography and cited in the text, so citations point at papers that actually exist.",
  },
];

const PaperVerseVsOverleaf = () => {
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        {
          "@type": "ListItem",
          position: 2,
          name: "PaperVerse Writer vs Overleaf",
          item: `${SITE_URL}/compare/paperverse-vs-overleaf`,
        },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title="PaperVerse Writer vs Overleaf: AI LaTeX Writing Compared"
        description="An honest comparison of PaperVerse Writer and Overleaf: AI drafting, literature search and citations, live LaTeX compiling, AI review, and when Overleaf is still the better fit."
        canonicalUrl={`${SITE_URL}/compare/paperverse-vs-overleaf`}
        keywords="overleaf alternative, overleaf ai, ai latex editor, latex editor online, ai academic writing assistant, ai paper writing tool"
        structuredData={structuredData}
      />

      <div className="min-h-screen flex flex-col">
        <Navbar />

        <main className="flex-1 w-full max-w-4xl mx-auto px-4 pt-28 pb-16">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            PaperVerse Writer vs Overleaf: which fits how you write papers?
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Overleaf is a mature online LaTeX editor built around writing and compiling
            documents together with your co-authors. PaperVerse Writer is an AI co-author with
            a LaTeX editor built in: it searches the literature, cites real sources, writes and
            revises sections with you, fixes compile errors, and can review the finished draft
            before a journal reviewer does.
          </p>

          <section className="grid gap-6 md:grid-cols-2 mt-10">
            <div className="rounded-xl border border-border bg-secondary/30 p-6">
              <h2 className="text-lg font-semibold mb-2">Pick PaperVerse Writer if</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                You want help producing the text itself: finding and citing the right papers,
                drafting a related work section, tightening an abstract, getting formulas and
                figures right, and hearing what a reviewer would say before you submit. Your
                work stays in the EU and stays private until you publish it.
              </p>
            </div>
            <div className="rounded-xl border border-border bg-secondary/30 p-6">
              <h2 className="text-lg font-semibold mb-2">Pick Overleaf if</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                You already know what you want to write, several co-authors need to type in the
                same document at the same time, and you rely on journal templates and an
                established institutional workflow. Overleaf has been built for exactly that
                for years.
              </p>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="text-2xl font-semibold mb-4">Side by side</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <caption className="sr-only">
                  Feature comparison between PaperVerse Writer and Overleaf
                </caption>
                <thead>
                  <tr className="border-b border-border text-left">
                    <th scope="col" className="py-3 pr-4 font-semibold align-bottom w-1/4">
                      What you need
                    </th>
                    <th scope="col" className="py-3 pr-4 font-semibold align-bottom">
                      PaperVerse Writer
                    </th>
                    <th scope="col" className="py-3 font-semibold align-bottom">
                      Overleaf
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.feature} className="border-b border-border/60 align-top">
                      <th scope="row" className="py-4 pr-4 font-medium text-left">
                        {row.feature}
                      </th>
                      <td className="py-4 pr-4 text-muted-foreground">{row.paperverse}</td>
                      <td className="py-4 text-muted-foreground">{row.overleaf}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              The Overleaf column describes what their product is widely known for. Overleaf
              changes their plans and features over time, so check their site for the current
              details before you decide.
            </p>
          </section>

          <section className="mt-12">
            <h2 className="text-2xl font-semibold mb-4">
              What Writer does that a plain LaTeX editor does not
            </h2>
            <ul className="space-y-3 text-muted-foreground list-disc pl-5 marker:text-primary/50">
              <li>
                Searches the literature, resolves each reference, adds it to the bibliography
                and places the citation in the right sentence.
              </li>
              <li>Drafts and revises sections in your own manuscript, not in a separate chat window.</li>
              <li>Works through the mathematics with you and checks the steps.</li>
              <li>Turns a described figure into a TikZ diagram inside the document.</li>
              <li>Reads the compiler log and repairs the LaTeX when a build breaks.</li>
              <li>
                Reviews the finished draft the way a conference referee would, with strengths,
                weaknesses and ratings.
              </li>
            </ul>
          </section>

          <section className="mt-12">
            <h2 className="text-2xl font-semibold mb-4">Where Overleaf is still the better choice</h2>
            <p className="text-muted-foreground leading-relaxed">
              If three co-authors need to edit the same file at once during a deadline night,
              if your institution already runs on Overleaf, or if you submit through a journal
              template that you want to open and fill in directly, Overleaf remains the
              stronger fit today. Writer is built around one researcher working with an AI
              co-author, and real-time co-editing is not part of it yet. Plenty of people use
              both: draft and check in PaperVerse, then finish the submission wherever their
              co-authors are.
            </p>
          </section>

          <section className="mt-12">
            <h2 className="text-2xl font-semibold mb-4">Questions people ask</h2>
            <div className="space-y-6">
              {faqs.map((item) => (
                <div key={item.q}>
                  <h3 className="font-medium mb-1">{item.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-14 rounded-2xl border border-border bg-secondary/30 p-8 text-center">
            <h2 className="text-2xl font-semibold mb-3">Try it on the section you are stuck on</h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Type what you need and watch it land in the manuscript. No account needed to
              start.
            </p>
            <Button asChild size="lg">
              <Link to="/write">Start writing</Link>
            </Button>
            <p className="text-xs text-muted-foreground mt-5">
              Your draft stays private until you publish · Written and processed in the EU ·
              Free to start
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              <Link to="/pricing" className="text-primary hover:underline">
                See plans and credits
              </Link>
              {" · "}
              <Link to="/faq" className="text-primary hover:underline">
                Read the full FAQ
              </Link>
            </p>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PaperVerseVsOverleaf;
